import React, { useState } from 'react'
import {
    Box,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    Alert,
    AlertTitle,
    Divider,
    CircularProgress,
    Chip,
} from '@mui/material'
import { styled, alpha } from '@mui/material/styles'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DescriptionIcon from '@mui/icons-material/Description'
import InfoIcon from '@mui/icons-material/Info'
import DownloadIcon from '@mui/icons-material/Download'
import { UPLOAD_USER_DATA } from 'api/UserConstants'
import sampleCsv from 'app/assets/sample.csv'

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
})

const UploadBox = styled(Paper)(({ theme }) => ({
    border: `2px dashed ${theme.palette.primary.light}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(5),
    textAlign: 'center',
    backgroundColor: theme.palette.background.default,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
    },
    '&:active': {
        transform: 'translateY(0)',
        borderColor: theme.palette.primary.dark,
        boxShadow: theme.shadows[2],
    },
}))

const UploadIcon = styled(CloudUploadIcon)(({ theme }) => ({
    fontSize: 60,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    transition: 'transform 0.2s ease',
    '${UploadBox}:hover &': {
        transform: 'scale(1.1)',
        color: theme.palette.primary.dark,
    },
}))

const ResultsContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
    maxHeight: '300px',
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
}))

const UploadUserCSV = ({ onUploadComplete }) => {
    const [file, setFile] = useState(null)
    const [createdUsers, setCreatedUsers] = useState([])
    const [validationErrors, setValidationErrors] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [uploadStatus, setUploadStatus] = useState(null)

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0]

        if (!selectedFile) {
            setUploadStatus({
                severity: 'warning',
                message: 'No file selected',
            })
            return
        }

        if (selectedFile.type !== 'text/csv') {
            setUploadStatus({
                severity: 'error',
                message: 'Invalid file type. Please upload a CSV file.',
            })
            return
        }

        setFile(selectedFile)
        setUploadStatus({
            severity: 'info',
            message: `File "${selectedFile.name}" ready for upload`,
        })
    }

    const uploadFile = async () => {
        if (!file) {
            setUploadStatus({
                severity: 'warning',
                message: 'Please select a file before uploading.',
            })
            return
        }

        setIsLoading(true)
        const formData = new FormData()
        formData.append('file', file)

        try {
            const response = await fetch(UPLOAD_USER_DATA, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: {
                    Accept: '*/*',
                },
            })

            const result = await response.json()

            if (response.ok) {
                setCreatedUsers(result.created_users || [])
                setValidationErrors(result.errors || [])

                const successCount = result.created_users?.length || 0
                const errorCount = result.errors?.length || 0
                const totalCount = successCount + errorCount

                if (successCount > 0) {
                    setUploadStatus({
                        severity: errorCount > 0 ? 'warning' : 'success',
                        message: `Processed ${totalCount} users: ${successCount} created successfully, ${errorCount} failed`,
                    })
                } else {
                    setUploadStatus({
                        severity: 'error',
                        message: `No users were created. All ${errorCount} entries had errors.`,
                    })
                }

                if (onUploadComplete) {
                    onUploadComplete(result)
                }
            } else {
                setValidationErrors(result.errors || [])
                setUploadStatus({
                    severity: 'error',
                    message:
                        result.error ||
                        result.message ||
                        'Failed to upload file. Please try again.',
                })
            }
        } catch (error) {
            console.error('An error occurred during file upload:', error)
            setUploadStatus({
                severity: 'error',
                message: 'An unexpected error occurred. Please try again.',
            })
        } finally {
            setIsLoading(false)
        }
    }

    const resetUpload = () => {
        setFile(null)
        setCreatedUsers([])
        setValidationErrors([])
        setUploadStatus(null)
    }

    return (
        <Box sx={{ width: '100%' }}>
            {!file && (
                <Paper
                    elevation={0}
                    variant="outlined"
                    color="info"
                    sx={{
                        p: 2,
                        mb: 3,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <InfoIcon
                            color="info"
                            sx={{ mt: 0.5, mr: 1.5, flexShrink: 0 }}
                        />
                        <Typography variant="body2" color="text.primary">
                            To upload users, please download the template CSV
                            file, fill it out, and then upload it here.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        href={sampleCsv}
                        download
                        color="primary"
                        size="small"
                        sx={{
                            whiteSpace: 'nowrap',
                            alignSelf: { xs: 'flex-start', sm: 'center' },
                            ml: { xs: 0, sm: 2 },
                            flexShrink: 0,
                        }}
                    >
                        Download Template
                    </Button>
                </Paper>
            )}

            {uploadStatus && (
                <Alert
                    severity={uploadStatus.severity}
                    sx={{ mt: 2, mb: 2 }}
                    onClose={() => setUploadStatus(null)}
                >
                    <AlertTitle>
                        {uploadStatus.severity === 'error'
                            ? 'Error'
                            : uploadStatus.severity === 'success'
                            ? 'Success'
                            : uploadStatus.severity === 'warning'
                            ? 'Partial Success'
                            : 'Info'}
                    </AlertTitle>
                    {uploadStatus.message}
                </Alert>
            )}

            {!file ? (
                <UploadBox component="label" htmlFor="csv-upload-input">
                    <UploadIcon />
                    <Typography
                        variant="h6"
                        component="div"
                        gutterBottom
                        fontWeight="medium"
                    >
                        Upload CSV File
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        Click to browse files
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        sx={{
                            borderRadius: '20px',
                            px: 3,
                            pointerEvents: 'none',
                        }}
                    >
                        Browse Files
                    </Button>
                    <VisuallyHiddenInput
                        id="csv-upload-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                    />
                </UploadBox>
            ) : (
                <Box sx={{ mb: 3 }}>
                    <Paper
                        sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <DescriptionIcon color="primary" />
                            <Typography variant="body1">{file.name}</Typography>
                            <Chip
                                label={`${(file.size / 1024).toFixed(1)} KB`}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={resetUpload}
                                disabled={isLoading}
                            >
                                Remove
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={uploadFile}
                                disabled={isLoading}
                                startIcon={
                                    isLoading ? (
                                        <CircularProgress size={20} />
                                    ) : null
                                }
                            >
                                {isLoading ? 'Uploading...' : 'Upload'}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}

            {(createdUsers.length > 0 || validationErrors.length > 0) && (
                <ResultsContainer>
                    {createdUsers.length > 0 && (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                }}
                            >
                                <CheckCircleOutlineIcon
                                    color="success"
                                    sx={{ mr: 1 }}
                                />
                                <Typography variant="h6" color="success.main">
                                    Created Users ({createdUsers.length})
                                </Typography>
                            </Box>
                            <List dense>
                                {createdUsers.map((user, index) => (
                                    <ListItem
                                        key={index}
                                        divider={
                                            index < createdUsers.length - 1
                                        }
                                    >
                                        <ListItemText primary={`${user.row}: ${user.email}`} />
                                    </ListItem>
                                ))}
                            </List>
                            {validationErrors.length > 0 && (
                                <Divider sx={{ my: 2 }} />
                            )}
                        </>
                    )}

                    {validationErrors.length > 0 && (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                }}
                            >
                                <ErrorOutlineIcon
                                    color="error"
                                    sx={{ mr: 1 }}
                                />
                                <Typography variant="h6" color="error">
                                    Errors ({validationErrors.length})
                                </Typography>
                            </Box>
                            <List dense>
                                {validationErrors.map((error, index) => (
                                    <ListItem
                                        key={index}
                                        divider={
                                            index < validationErrors.length - 1
                                        }
                                    >
                                        <ListItemText
                                            primary={`${error.row}: ${error.email}`}
                                            secondary={Object.entries(
                                                error.errors,
                                            )
                                                .map(
                                                    ([field, message]) =>
                                                        `${field}: ${message}`,
                                                )
                                                .join('\n')}
                                            secondaryTypographyProps={{
                                                style: {
                                                    whiteSpace: 'pre-line',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </ResultsContainer>
            )}
        </Box>
    )
}

export default UploadUserCSV
